import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatThemeLabel } from "../lib/format";
import { Flame, Target, TrendingUp, Instagram } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [analyses, setAnalyses] = useState([]);

  useEffect(() => {
    api.get("/puzzles/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/analyses").then(({ data }) => setAnalyses(data.analyses || [])).catch(() => {});
  }, []);

  const weakest = stats?.themes
    ? Object.entries(stats.themes).map(([t, s]) => ({ t, acc: s.total ? s.solved / s.total : 0, ...s }))
      .filter((x) => x.total >= 2).sort((a, b) => a.acc - b.acc).slice(0, 4)
    : [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="overline mb-2">Your board</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#EFE8D8]">
            Hello, {user?.name}.
          </h1>
        </div>
        <a
          href="https://instagram.com/instant_elo"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="dashboard-instagram"
          className="inline-flex items-center gap-2 text-xs text-[#C6A15B] px-3 py-1 border border-[#C6A15B]/40 rounded-sm font-mono uppercase tracking-widest hover:bg-[#C6A15B]/10 transition-colors"
        >
          <Instagram size={12} /> @instant_elo
        </a>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Elo estimate" value={stats?.elo_estimate ?? "—"} icon={TrendingUp} testid="stat-elo" />
        <StatCard label="Streak (days)" value={stats?.streak_days ?? 0} icon={Flame} testid="stat-streak" />
        <StatCard label="Accuracy" value={stats ? `${stats.accuracy}%` : "—"} icon={Target} testid="stat-accuracy" />
        <StatCard label="Puzzles solved" value={stats?.solved ?? 0} testid="stat-solved" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-panel p-6 sm:p-8">
          <div className="overline mb-2">Quick start</div>
          <h3 className="font-serif text-2xl text-[#EFE8D8] mb-6">Where do you want to go?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/puzzles" data-testid="quick-puzzles" className="btn-ghost text-left">Solve a puzzle →</Link>
            <Link to="/test-elo" data-testid="quick-test-elo" className="btn-ghost text-left">Test my Elo →</Link>
            <Link to="/analyze" data-testid="quick-analyze" className="btn-ghost text-left">Analyse a game →</Link>
            <Link to="/traps" data-testid="quick-traps" className="btn-ghost text-left">Learn a trap →</Link>
          </div>
        </div>

        <div className="card-panel p-6 sm:p-8">
          <div className="overline mb-2">Weakest themes</div>
          {weakest.length === 0 ? (
            <p className="text-sm text-[#A8A196]">Solve a few more puzzles to see patterns.</p>
          ) : (
            <ul className="space-y-2">
              {weakest.map((w) => (
                <li key={w.t} className="flex items-center justify-between text-sm font-mono">
                  <span className="text-[#EFE8D8]" data-testid={`weak-theme-${w.t}`}>{formatThemeLabel(w.t)}</span>
                  <span className="text-[#A04A3C]">{Math.round(w.acc * 100)}%</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-10">
        <div className="overline mb-2">Recent analyses</div>
        {analyses.length === 0 ? (
          <p className="text-sm text-[#A8A196]">No analyses yet. <Link to="/analyze" className="text-[#C6A15B] hover:underline">Analyse a game</Link>.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {analyses.slice(0, 6).map((a) => (
              <Link key={a.id} to={`/analyze/${a.id}`} data-testid={`analysis-${a.id}`} className="card-panel p-4 hover:bg-[#25211B] transition-colors">
                <div className="flex items-center justify-between">
                  <span className="font-serif text-lg text-[#EFE8D8]">{a.opening || "Unknown"}</span>
                  <span className="font-mono text-xs text-[#756F67]">avg {a.avg_cpl}cp</span>
                </div>
                <div className="mt-2 flex gap-3 text-xs font-mono text-[#A8A196]">
                  <span>Blunders: <span className="text-[#8B2E24]">{a.counts?.blunder ?? 0}</span></span>
                  <span>Mistakes: <span className="text-[#A04A3C]">{a.counts?.mistake ?? 0}</span></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="card-panel p-4 sm:p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="overline text-[10px]">{label}</span>
        {Icon && <Icon size={14} className="text-[#C6A15B]" />}
      </div>
      <div className="font-serif text-3xl text-[#EFE8D8]">{value}</div>
    </div>
  );
}
